/**
 * RhDocumentsPanel — pôle documents RH (LOT F).
 * Bibliothèque : 2 visibilités (tous agents / RH), téléchargeable ou lecture seule.
 */
import { useState, useEffect } from 'react';
import { Card, Button, Alert, Badge, Field } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { api, ApiException } from '@/lib/api';
import { FileText, Trash2, Plus } from 'lucide-react';
import { useAuth } from '@/lib/auth-context';
import type { RhDocument } from '@roronoa-collectivites/shared-types';

/**
 * Catégories (mig 239, items 5.9 / 5.11). Distinctes du `type`, qui reste la
 * nature métier du document : la catégorie dit comment il se comporte — une
 * note de service se diffuse et s'accuse, un modèle d'entretien se télécharge.
 */
const CATEGORIES = [
  { v: 'document', l: 'Document' },
  { v: 'note_service', l: 'Note de service' },
  { v: 'modele_entretien', l: "Modèle d'entretien" },
  { v: 'procedure', l: 'Procédure' },
  { v: 'reglement', l: 'Règlement' },
];

/** Codes de service, alignés sur la contrainte `users_service_valide`. */
const SERVICES = [
  { v: 'service_technique', l: 'Service technique' },
  { v: 'scolaire_cantine', l: 'Scolaire / Cantine' },
  { v: 'petite_enfance', l: 'Petite enfance' },
  { v: 'urbanisme', l: 'Urbanisme' },
  { v: 'associations', l: 'Associations' },
  { v: 'grc', l: 'GRC' },
  { v: 'rh', l: 'RH' },
  { v: 'comptable', l: 'Comptabilité' },
];

const TYPES = ['Déclaration incident / AT', 'Registre SST', 'DUERP', 'Règlement intérieur', 'Charte informatique', 'Livret d\'accueil', 'Fiche de poste', 'Entretien professionnel', 'Formation', 'Coordonnées (médecine travail / CDG)', 'Procédure', 'Autre'];

export function RhDocumentsPanel() {
  const { user } = useAuth();
  const peutGerer = user?.role === 'admin' || user?.role === 'super_admin' || (user?.role === 'manager' && user?.service === 'rh');
  const voitDocsRh = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'manager';
  const [docs, setDocs] = useState<RhDocument[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filtreVis, setFiltreVis] = useState<'tous' | 'agents' | 'rh'>('tous');
  const [recherche, setRecherche] = useState('');
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState<{
    titre: string; type: string;
    visibilite: 'agents' | 'rh';
    modeAcces: 'telechargeable' | 'lecture_seule';
    categorie: string; dateEffet: string; services: string[]; remplaceId: string;
  }>({
    titre: '', type: TYPES[0]!, visibilite: 'agents', modeAcces: 'telechargeable',
    categorie: 'document', dateEffet: '', services: [], remplaceId: '',
  });
  // Filtres 5.11 — appliqués côté SERVEUR : filtrer en mémoire ne marcherait
  // pas sur les abrogés, qui ne sont pas chargés par défaut.
  const [filtreCategorie, setFiltreCategorie] = useState('');
  const [voirAbroges, setVoirAbroges] = useState(false);
  const [lectures, setLectures] = useState<{
    id: string; lus: Array<{ nom: string; luAt: string }>; nonLus: string[];
  } | null>(null);
  const [fichier, setFichier] = useState<File | null>(null);
  const [busyAjout, setBusyAjout] = useState(false);

  async function charger() {
    try {
      setDocs(await api.rh.listDocuments({
        categorie: filtreCategorie || undefined,
        statut: voirAbroges ? 'abroge' : undefined,
        recherche: recherche || undefined,
      }));
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  useEffect(() => { void charger(); }, [filtreCategorie, voirAbroges, recherche]);

  async function ajouter() {
    setError(null);
    setBusyAjout(true);
    try {
      await api.rh.creerDocument(form, fichier);
      setOpen(false);
      setForm({
        titre: '', type: TYPES[0]!, visibilite: 'agents', modeAcces: 'telechargeable',
        categorie: 'document', dateEffet: '', services: [], remplaceId: '',
      });
      setFichier(null);
      await charger();
    }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setBusyAjout(false); }
  }
  async function telecharger(id: string) {
    try { const { url } = await api.rh.documentFichier(id); window.open(url, '_blank'); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Téléchargement impossible'); }
  }
  async function supprimer(id: string) {
    setError(null);
    try { await api.rh.supprimerDocument(id); await charger(); }
    catch (e) {
      // 409 attendu sur un document diffusé : le message du serveur explique
      // qu'il faut l'abroger, pas l'effacer. On le montre plutôt que de
      // l'avaler comme avant.
      setError(e instanceof ApiException ? e.message : 'Suppression impossible');
    }
  }

  async function diffuser(id: string) {
    setError(null);
    try {
      const r = await api.rh.diffuserDocument(id);
      setError(null);
      await charger();
      alertDiffusion(r.destinataires);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : 'Diffusion impossible');
    }
  }

  const [messageDiffusion, setMessageDiffusion] = useState<string | null>(null);
  function alertDiffusion(n: number) {
    setMessageDiffusion(`Note diffusée à ${n} agent${n > 1 ? 's' : ''}.`);
  }

  async function abroger(id: string) {
    setError(null);
    try { await api.rh.abrogerDocument(id); await charger(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Abrogation impossible'); }
  }

  async function accuser(id: string) {
    try { await api.rh.accuserLectureDocument(id); await charger(); } catch { /* ignore */ }
  }

  async function voirLectures(id: string) {
    try {
      const r = await api.rh.lecturesDocument(id);
      setLectures({ id, ...r });
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Lecture impossible'); }
  }

  const filtres = docs.filter((d) => filtreVis === 'tous' || d.visibilite === filtreVis);

  return (
    <div className="space-y-5">
      {error && <Alert variant="error">{error}</Alert>}
      {messageDiffusion && (
        <Alert variant="success">{messageDiffusion}</Alert>
      )}

      {/* Documents-types à en-tête mairie */}
      {peutGerer && <ModelesTypes />}

      <Card className="overflow-hidden p-0">
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          <h2 className="font-display text-sm font-semibold text-ink">Documents RH</h2>
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <input type="text" value={recherche} onChange={(e) => setRecherche(e.target.value)} placeholder="Rechercher…" className="h-8 rounded-r-md border border-border bg-surface/60 px-2 text-xs text-ink" />
            {voitDocsRh && (
              <div className="inline-flex rounded-r-md border border-border-hi bg-surface/60 p-0.5">
                {(['tous', 'agents', 'rh'] as const).map((f) => (
                  <button key={f} type="button" onClick={() => setFiltreVis(f)} className={`rounded-r-sm px-2.5 py-1 text-xs ${filtreVis === f ? 'bg-rh text-white' : 'text-ink-muted'}`}>{f === 'tous' ? 'Tous' : f === 'agents' ? 'Agents' : 'RH'}</button>
                ))}
              </div>
            )}
            <select value={filtreCategorie} onChange={(e) => setFiltreCategorie(e.target.value)}
              aria-label="Filtrer par catégorie"
              className="h-8 rounded-r-md border border-border bg-surface/60 px-2 text-xs text-ink">
              <option value="">Toutes catégories</option>
              {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
            </select>
            {/* Les abrogés sont derrière un interrupteur : par défaut on ne voit
                que ce qui s'applique aujourd'hui. */}
            <button type="button" onClick={() => setVoirAbroges((v) => !v)}
              aria-pressed={voirAbroges}
              className={`rounded-r-md border px-2.5 py-1 text-xs ${voirAbroges ? 'border-rh/50 bg-rh/10 text-ink' : 'border-border-hi text-ink-muted'}`}>
              {voirAbroges ? 'Archives affichées' : 'Voir les abrogés'}
            </button>
            {peutGerer && <Button variant="secondary" size="sm" onClick={() => setOpen(true)}><Plus className="h-4 w-4" /> Ajouter</Button>}
          </div>
        </div>
        {filtres.length === 0 ? (
          <p className="px-4 py-6 text-center text-sm text-ink-subtle">Aucun document.</p>
        ) : (
          <ul className="divide-y divide-border">
            {filtres.map((d) => (
              <li key={d.id} className="flex items-center gap-3 px-4 py-3">
                <span className="inline-grid h-9 w-9 shrink-0 place-items-center rounded-r-lg bg-rh-tint text-rh-fg"><FileText className="h-4 w-4" /></span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-ink">{d.titre}</div>
                  <div className="flex flex-wrap items-center gap-1.5 text-xs text-ink-subtle">
                    <span>{d.type}</span>
                    <Badge variant={d.visibilite === 'rh' ? 'neutral' : 'info'}>{d.visibilite === 'rh' ? 'RH seulement' : 'Tous agents'}</Badge>
                    {d.modeAcces === 'lecture_seule' && <Badge variant="neutral">Lecture seule</Badge>}
                    {d.categorie !== 'document' && (
                      <Badge variant="info">
                        {CATEGORIES.find((c) => c.v === d.categorie)?.l ?? d.categorie}
                      </Badge>
                    )}
                    {d.statut === 'abroge' && <Badge variant="urgent">Abrogé</Badge>}
                    {d.services.length > 0 && (
                      <span>{d.services.map((sv) => SERVICES.find((x) => x.v === sv)?.l ?? sv).join(', ')}</span>
                    )}
                  </div>
                  {/* Ce qui rend l'historique lisible : d'où vient ce document,
                      et quand il a cessé de s'appliquer. */}
                  {d.remplaceTitre && (
                    <p className="mt-0.5 text-2xs text-ink-subtle">
                      Remplace « {d.remplaceTitre} »
                    </p>
                  )}
                  {d.statut === 'abroge' && d.dateAbrogation && (
                    <p className="mt-0.5 text-2xs text-ink-subtle">
                      Abrogé le {d.dateAbrogation.split('-').reverse().join('/')}
                    </p>
                  )}
                  {d.categorie === 'note_service' && d.diffuseAt && (
                    <p className="mt-0.5 text-2xs text-ink-subtle">
                      Diffusée · {d.lecturesCount} accusé{d.lecturesCount > 1 ? 's' : ''} de lecture
                    </p>
                  )}
                </div>

                {/* L'agent accuse réception d'une note qui le concerne. */}
                {d.categorie === 'note_service' && d.statut === 'en_vigueur' && !peutGerer && (
                  <button type="button" onClick={() => void accuser(d.id)}
                    disabled={d.luParMoi}
                    className={`rounded-r-md border px-2.5 py-1 text-xs ${d.luParMoi ? 'border-border bg-surface/40 text-ink-subtle' : 'border-rh/50 text-ink hover:bg-rh/10'}`}>
                    {d.luParMoi ? 'Lu' : "J'ai lu"}
                  </button>
                )}

                {peutGerer && d.categorie === 'note_service' && !d.diffuseAt && d.statut === 'en_vigueur' && (
                  <button type="button" onClick={() => void diffuser(d.id)}
                    className="rounded-r-md border border-rh/50 px-2.5 py-1 text-xs text-ink hover:bg-rh/10">
                    Diffuser
                  </button>
                )}
                {peutGerer && d.diffuseAt && (
                  <button type="button" onClick={() => void voirLectures(d.id)}
                    className="rounded-r-md border border-border-hi px-2.5 py-1 text-xs text-ink-muted hover:text-ink">
                    Qui a lu ?
                  </button>
                )}
                {peutGerer && d.statut === 'en_vigueur' && (
                  <button type="button" onClick={() => void abroger(d.id)}
                    className="rounded-r-md border border-border-hi px-2.5 py-1 text-xs text-ink-muted hover:text-ink">
                    Abroger
                  </button>
                )}
                {d.r2Key && (
                  <button type="button" onClick={() => void telecharger(d.id)} className="rounded-r-md border border-border-hi px-2.5 py-1 text-xs text-ink-muted hover:text-ink">
                    {d.modeAcces === 'lecture_seule' ? 'Voir' : 'Télécharger'}
                  </button>
                )}
                {peutGerer && <button type="button" onClick={() => void supprimer(d.id)} className="text-ink-subtle hover:text-danger"><Trash2 className="h-4 w-4" /></button>}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Dialog open={open} onClose={() => setOpen(false)} title="Ajouter un document"
        footer={<><Button variant="ghost" onClick={() => setOpen(false)}>Annuler</Button><Button variant="rh" disabled={!form.titre.trim() || busyAjout} onClick={() => void ajouter()}>{busyAjout ? 'Ajout…' : 'Ajouter'}</Button></>}>
        <div className="space-y-3">
          <Field label="Titre" htmlFor="doc-titre">
            <input id="doc-titre" type="text" value={form.titre} onChange={(e) => setForm((f) => ({ ...f, titre: e.target.value }))} className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm text-ink" />
          </Field>
          <Field label="Type" htmlFor="doc-type">
            <select id="doc-type" value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm text-ink">
              {TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Visibilité" htmlFor="doc-vis">
              <select id="doc-vis" value={form.visibilite} onChange={(e) => setForm((f) => ({ ...f, visibilite: e.target.value as 'agents' | 'rh' }))} className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm text-ink">
                <option value="agents">Tous les agents</option>
                <option value="rh">RH seulement</option>
              </select>
            </Field>
            <Field label="Accès" htmlFor="doc-acces">
              <select id="doc-acces" value={form.modeAcces} onChange={(e) => setForm((f) => ({ ...f, modeAcces: e.target.value as 'telechargeable' | 'lecture_seule' }))} className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm text-ink">
                <option value="telechargeable">Téléchargeable</option>
                <option value="lecture_seule">Lecture seule</option>
              </select>
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Catégorie" htmlFor="doc-cat">
              <select id="doc-cat" value={form.categorie}
                onChange={(e) => setForm((f) => ({ ...f, categorie: e.target.value }))}
                className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm text-ink">
                {CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.l}</option>)}
              </select>
            </Field>
            <Field label="Date d'effet" htmlFor="doc-effet">
              <input id="doc-effet" type="date" value={form.dateEffet}
                onChange={(e) => setForm((f) => ({ ...f, dateEffet: e.target.value }))}
                className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm text-ink" />
            </Field>
          </div>

          <Field label="Services concernés" htmlFor="doc-services">
            <div className="flex flex-wrap gap-1.5" id="doc-services">
              {SERVICES.map((sv) => {
                const actif = form.services.includes(sv.v);
                return (
                  <button key={sv.v} type="button" aria-pressed={actif}
                    onClick={() => setForm((f) => ({
                      ...f,
                      services: actif ? f.services.filter((x) => x !== sv.v) : [...f.services, sv.v],
                    }))}
                    className={`rounded-r-md border px-2.5 py-1 text-xs ${actif ? 'border-rh/50 bg-rh/10 text-ink' : 'border-border text-ink-muted'}`}>
                    {sv.l}
                  </button>
                );
              })}
            </div>
            <p className="mt-1 text-xs text-ink-subtle">
              Aucun service coché = toute la commune.
            </p>
          </Field>

          {/* 5.10 — le geste qui abroge automatiquement l'ancien document. */}
          <Field label="Remplace un document existant" htmlFor="doc-remplace">
            <select id="doc-remplace" value={form.remplaceId}
              onChange={(e) => setForm((f) => ({ ...f, remplaceId: e.target.value }))}
              className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm text-ink">
              <option value="">— Aucun —</option>
              {docs.filter((d) => d.statut === 'en_vigueur').map((d) => (
                <option key={d.id} value={d.id}>{d.titre}</option>
              ))}
            </select>
            <p className="mt-1 text-xs text-ink-subtle">
              Le document choisi sera <strong>abrogé automatiquement</strong> à la
              date d'effet de celui-ci.
            </p>
          </Field>

          <Field label="Fichier (PDF ou Word)" htmlFor="doc-fichier">
            <input id="doc-fichier" type="file" accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
              onChange={(e) => setFichier(e.target.files?.[0] ?? null)}
              className="block w-full text-sm text-ink-muted file:mr-3 file:rounded-r-md file:border-0 file:bg-rh file:px-3 file:py-1.5 file:text-xs file:font-medium file:text-white" />
          </Field>
          <p className="text-xs text-ink-subtle">Ajoutez votre document rempli (PDF de préférence). Sans fichier, seule une référence titre + type est créée.</p>
        </div>
      </Dialog>
      <Dialog open={lectures !== null} onClose={() => setLectures(null)}
        title="Accusés de lecture"
        footer={<Button variant="ghost" onClick={() => setLectures(null)}>Fermer</Button>}>
        {lectures && (
          <div className="space-y-3 text-sm">
            <div>
              <p className="font-medium text-ink">Ont lu ({lectures.lus.length})</p>
              {lectures.lus.length === 0 ? (
                <p className="text-xs text-ink-subtle">Personne pour l'instant.</p>
              ) : (
                <ul className="mt-1 text-xs text-ink-muted">
                  {lectures.lus.map((l) => <li key={l.nom}>{l.nom}</li>)}
                </ul>
              )}
            </div>
            <div>
              <p className="font-medium text-ink">N'ont pas lu ({lectures.nonLus.length})</p>
              {lectures.nonLus.length === 0 ? (
                <p className="text-xs text-ink-subtle">Tout le monde a accusé réception.</p>
              ) : (
                <ul className="mt-1 text-xs text-ink-muted">
                  {lectures.nonLus.map((n) => <li key={n}>{n}</li>)}
                </ul>
              )}
            </div>
          </div>
        )}
      </Dialog>
    </div>
  );
}

/** Bloc « Document à en-tête » : télécharge un Word vierge à en-tête mairie. */
function ModelesTypes() {
  const [busy, setBusy] = useState(false);
  async function tele() {
    setBusy(true);
    try { await api.rh.telechargerDocumentEntete(); } catch { /* ignore */ }
    finally { setBusy(false); }
  }
  return (
    <Card className="p-4">
      <h2 className="mb-1 font-display text-sm font-semibold text-ink">Document à l'en-tête de la mairie</h2>
      <p className="mb-3 text-xs text-ink-muted">Un document Word vierge avec votre logo, vos coordonnées et votre pied de page. Téléchargez-le, rédigez votre document (règlement intérieur, note de service…), exportez-le en PDF, puis remettez-le à disposition via « Ajouter ».</p>
      <Button variant="secondary" size="sm" disabled={busy} onClick={() => void tele()}>
        <FileText className="h-4 w-4" /> Télécharger un document à en-tête (Word)
      </Button>
    </Card>
  );
}
